import React from "react";
import { useTranslation } from "react-i18next";
import { PackageCheck, AlertTriangle, XCircle, Printer, FileDown, Warehouse, User, Store } from "lucide-react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";

function Stat({ icon: Icon, value, label, tone }) {
  const toneText = {
    success: "text-status-success",
    warning: "text-status-warning",
    error: "text-status-error",
  }[tone] || "text-fg";
  return (
    <div className="rounded-xl border border-border p-3 text-center">
      <Icon className={`mx-auto h-5 w-5 ${toneText}`} />
      <p className={`mt-1 text-2xl font-bold tabular-nums ${toneText}`}>{value}</p>
      <p className="text-xs text-fg-muted">{label}</p>
    </div>
  );
}

export default function FinishSessionModal({
  open, onClose, onConfirm, onDownload,
  total, breakdown, byComercio, bodegaName, clientName, mismatchCount = 0, operatorName, periodLabel, correlativo,
}) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Resumen de recepción")}
      description={t("Cierra la sesión y genera el comprobante del retiro recibido en bodega.")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t("Seguir recibiendo")}</Button>
          <Button variant="outline" iconLeft={<FileDown className="h-4 w-4" />} onClick={onDownload}>{t("Descargar comprobante")}</Button>
          <Button variant="primary" iconLeft={<Printer className="h-4 w-4" />} onClick={onConfirm}>{t("Cerrar e iniciar nueva")}</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-bg-muted px-4 py-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{t("N° de retiro")}</p>
            <p className="font-mono text-lg font-semibold text-fg">{correlativo}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-fg-muted">
            <span className="inline-flex items-center gap-1.5"><Store className="h-4 w-4" />{clientName || t("Sin asignar")}</span>
            <span className="inline-flex items-center gap-1.5"><Warehouse className="h-4 w-4" />{bodegaName}</span>
            <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" />{operatorName}</span>
            <span>{periodLabel}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Stat icon={PackageCheck} value={breakdown.success} label={t("Recolectados")} tone="success" />
          <Stat icon={AlertTriangle} value={breakdown.duplicate} label={t("Repetidos")} tone="warning" />
          <Stat icon={XCircle} value={breakdown.notfound} label={t("No encontrados")} tone="error" />
        </div>

        <section className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{t("Por comercio")}</h3>
          <div className="rounded-xl border border-border divide-y divide-border">
            {byComercio.length === 0 ? (
              <p className="px-4 py-3 text-sm text-fg-muted">{t("Sin recepciones válidas.")}</p>
            ) : (
              byComercio.map((c) => (
                <div key={c.name} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-fg truncate">{c.name}</span>
                  <span className="text-sm font-semibold text-fg tabular-nums">{c.count}</span>
                </div>
              ))
            )}
          </div>
          <p className="text-xs text-fg-muted">{t("Total recolectados")}: <span className="font-semibold text-fg">{total}</span></p>
        </section>

        {mismatchCount > 0 && clientName && (
          <div className="flex items-center gap-2 rounded-lg bg-status-warning-soft px-3 py-2 text-sm text-status-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{t("{{n}} pedido(s) recibido(s) son de otro comercio distinto a {{cliente}}.", { n: mismatchCount, cliente: clientName })}</span>
          </div>
        )}

        <PendingBackendNotice>
          {t("El comprobante PDF y el registro del retiro están en construcción.")}
        </PendingBackendNotice>
      </div>
    </Modal>
  );
}
